export type ReferenceImageOptimizationProfile = "balanced" | "compact";

export type ReferenceImageOptimizer = (
  dataUrl: string,
  profile: ReferenceImageOptimizationProfile,
) => Promise<string>;

interface PrepareReferenceImageOptions {
  optimizer?: ReferenceImageOptimizer;
  targetPerImageCharacters?: number;
  targetTotalCharacters?: number;
  hardPerImageCharacters?: number;
  hardTotalCharacters?: number;
}

export const referenceImageUploadBudget = {
  targetPerImageCharacters: 650_000,
  targetTotalCharacters: 1_800_000,
  hardPerImageCharacters: 850_000,
  hardTotalCharacters: 2_500_000,
} as const;

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("无法读取压缩后的参考图片"));
    reader.readAsDataURL(blob);
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("浏览器无法压缩这张参考图片")),
      type,
      quality,
    );
  });
}

export const optimizeReferenceImageInBrowser: ReferenceImageOptimizer = async (
  dataUrl,
  profile,
) => {
  const response = await fetch(dataUrl);
  const source = await response.blob();
  const bitmap = await createImageBitmap(source);
  try {
    const maxEdge = profile === "compact" ? 1024 : 1400;
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("浏览器无法处理参考图片");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const compressed = await canvasToBlob(
      canvas,
      "image/webp",
      profile === "compact" ? 0.72 : 0.8,
    );
    const result = await blobToDataUrl(compressed);
    return result.length < dataUrl.length ? result : dataUrl;
  } finally {
    bitmap.close();
  }
};

export async function prepareImageGenerationReferences(
  referenceImages: string[],
  options: PrepareReferenceImageOptions = {},
) {
  if (!referenceImages.length) throw new Error("请至少选择 1 张产品参考图");
  if (referenceImages.length > 4)
    throw new Error("产品参考图最多选择 4 张");
  if (referenceImages.some((image) => !image.startsWith("data:image/")))
    throw new Error("参考图片格式不正确，请重新上传");

  const optimizer = options.optimizer ?? optimizeReferenceImageInBrowser;
  const targetPerImage =
    options.targetPerImageCharacters ??
    referenceImageUploadBudget.targetPerImageCharacters;
  const targetTotal =
    options.targetTotalCharacters ?? referenceImageUploadBudget.targetTotalCharacters;
  const hardPerImage =
    options.hardPerImageCharacters ??
    referenceImageUploadBudget.hardPerImageCharacters;
  const hardTotal =
    options.hardTotalCharacters ?? referenceImageUploadBudget.hardTotalCharacters;
  const originalTotal = referenceImages.reduce(
    (total, image) => total + image.length,
    0,
  );
  if (
    originalTotal <= targetTotal &&
    referenceImages.every((image) => image.length <= targetPerImage)
  ) {
    return referenceImages;
  }

  let prepared = await Promise.all(
    referenceImages.map((image) =>
      originalTotal > targetTotal || image.length > targetPerImage
        ? optimizer(image, "balanced")
        : Promise.resolve(image),
    ),
  );
  const balancedTotal = prepared.reduce(
    (total, image) => total + image.length,
    0,
  );
  if (
    balancedTotal > targetTotal ||
    prepared.some((image) => image.length > targetPerImage)
  ) {
    prepared = await Promise.all(
      prepared.map((image) =>
        balancedTotal > targetTotal || image.length > targetPerImage
          ? optimizer(image, "compact")
          : Promise.resolve(image),
      ),
    );
  }

  const preparedTotal = prepared.reduce(
    (total, image) => total + image.length,
    0,
  );
  if (
    preparedTotal > hardTotal ||
    prepared.some((image) => image.length > hardPerImage)
  ) {
    throw new Error("参考图片压缩后仍然过大，请删除大图后重新上传");
  }
  return prepared;
}
